import AutoTextarea from './AutoTextarea';
import React, { useEffect, useState } from 'react';
import api from '../services/api';

// Интерфейсы
interface InvestmentAsset {
  id: number;
  name: string;
  ticker: string | null;
  type: 'stock' | 'crypto' | 'bond' | 'etf' | 'currency' | 'commodity' | 'other';
  investingUrl: string | null;
  currentPrice: number | null;
  currency: string;
  lastPriceUpdate: string | null;
  notes: string | null;
  stats?: {
    totalQuantity: number;
    averagePrice: number;
    averagePriceCurrency?: string;
    totalInvested: number;
    totalReceived: number;
    netInvested: number;
    currentValue: number;
    profit: number;
    profitPercent: number;
    displayCurrency?: string;
  };
  transactions?: InvestmentTransaction[];
}

interface InvestmentTransaction {
  id: number;
  assetId: number;
  type: 'buy' | 'sell';
  quantity: number;
  price: number;
  totalAmount: number;
  transactionDate: string;
  commission: number;
  notes: string | null;
}

const InvestmentsPortfolio: React.FC = () => {
  const [assets, setAssets] = useState<InvestmentAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [parsing, setParsing] = useState(false);
  const [parseSuccess, setParseSuccess] = useState(false);
  const [parseError, setParseError] = useState('');
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<InvestmentAsset | null>(null);
  const [updatingAllPrices, setUpdatingAllPrices] = useState(false); // Флаг обновления всех цен
  const [updateResult, setUpdateResult] = useState<string>(''); // Результат обновления

  // Форма добавления актива
  const [assetForm, setAssetForm] = useState({
    url: '',
    name: '',
    ticker: '',
    type: 'stock' as InvestmentAsset['type'],
    currentPrice: '',
    currency: 'USD',
    quantity: '', // Количество единиц актива
    notes: ''
  });

  // Хранение оригинальной цены и валюты (для конвертации при смене валюты в форме)
  const [originalPrice, setOriginalPrice] = useState<number | null>(null);
  const [originalCurrency, setOriginalCurrency] = useState<string>('USD');

  // Конвертация валют
  const [targetCurrency, setTargetCurrency] = useState<string>('RUB'); // Валюта для отображения
  const [convertedPrices, setConvertedPrices] = useState<{ [key: number]: number }>({}); // ID актива -> конвертированная цена
  const [converting, setConverting] = useState(false);

  // Форма транзакции
  const [transactionForm, setTransactionForm] = useState({
    type: 'buy' as 'buy' | 'sell',
    quantity: '',
    price: '',
    date: new Date().toISOString().split('T')[0],
    commission: '0',
    notes: ''
  });

  useEffect(() => {
    loadAssets();
  }, []);

  // Перезагружаем активы при смене валюты (бэкенд пересчитает статистику)
  useEffect(() => {
    if (!loading) {
      loadAssets();
    }
  }, [targetCurrency]);

  const loadAssets = async () => {
    try {
      // Передаем targetCurrency чтобы бэкенд конвертировал все суммы
      const response = await api.get('/api/investment-assets', {
        params: { targetCurrency }
      });
      setAssets(response.data);
    } catch (error) {
      console.error('Failed to load assets:', error);
    } finally {
      setLoading(false);
    }
  };

  // Конвертирует цены всех активов в целевую валюту
  const convertAllPrices = async () => {
    if (converting) return;
    
    setConverting(true);
    const newConvertedPrices: { [key: number]: number } = {};
    
    try {
      // Конвертируем цены параллельно для всех активов
      await Promise.all(
        assets.map(async (asset) => {
          if (asset.currentPrice) {
            try {
              const response = await api.get('/api/currency/convert', {
                params: {
                  amount: asset.currentPrice,
                  from: asset.currency,
                  to: targetCurrency
                }
              });
              newConvertedPrices[asset.id] = response.data.converted.amount;
            } catch (error) {
              console.error(`Failed to convert ${asset.currency} to ${targetCurrency}:`, error);
              // В случае ошибки используем исходную цену
              newConvertedPrices[asset.id] = asset.currentPrice;
            }
          }
        })
      );
      
      setConvertedPrices(newConvertedPrices);
    } finally {
      setConverting(false);
    }
  };

  // Конвертирует цену в форме при смене валюты
  const handleCurrencyChange = async (newCurrency: string) => {
    const currentPrice = parseFloat(assetForm.currentPrice);
    
    // Если цена не введена или некорректна - просто меняем валюту
    if (!currentPrice || isNaN(currentPrice) || currentPrice <= 0) {
      setAssetForm({ ...assetForm, currency: newCurrency });
      return;
    }
    
    // Если цена есть - конвертируем её
    try {
      const response = await api.get('/api/currency/convert', {
        params: {
          amount: currentPrice,
          from: assetForm.currency, // Текущая валюта
          to: newCurrency // Новая валюта
        }
      });
      
      const convertedAmount = response.data.converted.amount;
      
      setAssetForm({ 
        ...assetForm, 
        currency: newCurrency,
        currentPrice: convertedAmount.toFixed(2) // Округляем до 2 знаков
      });
      
      console.log(`💱 Converted ${currentPrice} ${assetForm.currency} → ${convertedAmount.toFixed(2)} ${newCurrency}`);
      
    } catch (error) {
      console.error('Failed to convert currency:', error);
      // В случае ошибки просто меняем валюту без конвертации
      setAssetForm({ ...assetForm, currency: newCurrency });
    }
  };

  const handleParseUrl = async () => {
    if (!assetForm.url.trim()) {
      setParseError('Введите ссылку с investing.com');
      return;
    }

    setParsing(true);
    setParseError('');
    setParseSuccess(false);
    
    try {
      const response = await api.post('/api/investment-assets/parse-url', {
        url: assetForm.url
      });

      const data = response.data.data;
      
      // Сохраняем оригинальную цену и валюту (для последующей конвертации)
      setOriginalPrice(data.price || null);
      setOriginalCurrency(data.currency || 'USD');
      
      // Автоматически заполняем все поля формы
      setAssetForm({
        url: assetForm.url, // Сохраняем введённый URL
        name: data.name || '',
        ticker: data.ticker || '',
        type: data.type || 'other',
        currentPrice: data.price?.toString() || '',
        currency: data.currency || 'USD',
        quantity: assetForm.quantity, // Сохраняем количество если было введено
        notes: assetForm.notes // Сохраняем заметки если были введены
      });

      // Показываем успех
      setParseSuccess(true);
      console.log('✅ Данные успешно получены:', data);
      
      // Автоматически скрываем сообщение об успехе через 3 секунды
      setTimeout(() => setParseSuccess(false), 3000);
      
    } catch (error: any) {
      let errorMsg = error.response?.data?.message || 'Ошибка при парсинге. Проверьте ссылку.';
      
      // Если это ошибка "не найдено" - показываем более понятное сообщение
      if (errorMsg.includes('не найдена на Мосбирже') || errorMsg.includes('не найден')) {
        setParseError(
          `❌ ${errorMsg}\n\n` +
          `💡 Попробуйте:\n` +
          `• Тикер: SBER, GAZP, AAPL, BTC\n` +
          `• Другое название компании\n` +
          `• Полную ссылку с investing.com`
        );
      } else {
        setParseError(errorMsg);
      }
      
      console.error('Ошибка парсинга:', error);
    } finally {
      setParsing(false);
    }
  };

  const handleCreateAsset = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Создаём актив
      const response = await api.post('/api/investment-assets', {
        name: assetForm.name,
        ticker: assetForm.ticker || null,
        type: assetForm.type,
        investingUrl: assetForm.url || null,
        currentPrice: assetForm.currentPrice ? parseFloat(assetForm.currentPrice) : null,
        currency: assetForm.currency,
        notes: assetForm.notes || null
      });

      const createdAsset = response.data;

      // Если указано количество, создаём транзакцию покупки
      if (assetForm.quantity && parseFloat(assetForm.quantity) > 0) {
        const quantity = parseFloat(assetForm.quantity);
        const price = assetForm.currentPrice ? parseFloat(assetForm.currentPrice) : 0;
        
        await api.post(`/api/investment-assets/${createdAsset.id}/transactions`, {
          type: 'buy',
          quantity: quantity,
          price: price,
          totalAmount: quantity * price,
          transactionDate: new Date().toISOString().split('T')[0],
          commission: 0,
          notes: 'Начальная покупка'
        });
      }

      setAssetForm({
        url: '',
        name: '',
        ticker: '',
        type: 'stock',
        currentPrice: '',
        currency: 'USD',
        quantity: '',
        notes: ''
      });
      setShowAddAsset(false);
      loadAssets();
      alert('✅ Актив добавлен!' + (assetForm.quantity ? ' Транзакция покупки создана.' : ''));
    } catch (error: any) {
      alert(error.response?.data?.message || 'Ошибка при создании актива');
    }
  };

  const handleUpdatePrice = async (asset: InvestmentAsset) => {
    if (!asset.investingUrl) {
      alert('У актива нет ссылки для обновления цены');
      return;
    }

    try {
      const response = await api.post(`/api/investment-assets/${asset.id}/update-price`);
      alert(`✅ Цена обновлена: ${response.data.currentPrice} ${asset.currency}`);
      loadAssets();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Ошибка при обновлении цены');
    }
  };

  // Обновить цены всех активов сразу
  const handleUpdateAllPrices = async () => {
    setUpdatingAllPrices(true);
    setUpdateResult('');

    try {
      const response = await api.post('/api/investment-assets/update-all-prices');
      const { successful, failed, total } = response.data;
      
      setUpdateResult(`✅ Обновлено: ${successful} из ${total}`);
      
      // Перезагружаем активы
      await loadAssets();
      
      // Скрываем сообщение через 5 секунд
      setTimeout(() => setUpdateResult(''), 5000);
      
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка при обновлении цен';
      setUpdateResult(`❌ ${errorMsg}`);
      setTimeout(() => setUpdateResult(''), 5000);
    } finally {
      setUpdatingAllPrices(false);
    }
  };

  const handleDeleteAsset = async (id: number) => {
    if (!window.confirm('Удалить актив? Все транзакции по нему также будут удалены.')) return;

    try {
      await api.delete(`/api/investment-assets/${id}`);
      loadAssets();
    } catch (error) {
      alert('Ошибка при удалении');
    }
  };

  const handleAddTransaction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAsset) return;

    const quantity = parseFloat(transactionForm.quantity);
    const availableQuantity = selectedAsset.stats?.totalQuantity || 0;

    // Валидация: нельзя продать больше чем есть
    if (transactionForm.type === 'sell' && quantity > availableQuantity) {
      alert(
        `❌ Недостаточно активов для продажи!\n\n` +
        `У вас есть: ${availableQuantity.toFixed(8)} ${selectedAsset.ticker || selectedAsset.name}\n` +
        `Вы пытаетесь продать: ${quantity.toFixed(8)}`
      );
      return;
    }

    try {
      await api.post(`/api/investment-assets/${selectedAsset.id}/transactions`, {
        type: transactionForm.type,
        quantity: quantity,
        price: parseFloat(transactionForm.price),
        transactionDate: transactionForm.date,
        commission: parseFloat(transactionForm.commission),
        notes: transactionForm.notes || null
      });

      setTransactionForm({
        type: 'buy',
        quantity: '',
        price: '',
        date: new Date().toISOString().split('T')[0],
        commission: '0',
        notes: ''
      });
      setShowTransactionModal(false);
      setSelectedAsset(null);
      loadAssets();
      alert('✅ Транзакция добавлена!');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Ошибка при добавлении транзакции');
    }
  };

  // Расчёт общей статистики
  const totalInvested = assets.reduce((sum, a) => sum + (a.stats?.netInvested || 0), 0);
  const totalCurrentValue = assets.reduce((sum, a) => sum + (a.stats?.currentValue || 0), 0);
  const totalProfit = totalCurrentValue - totalInvested;
  const profitPercent = totalInvested > 0 ? (totalProfit / totalInvested) * 100 : 0;

  if (loading) return <div style={{ textAlign: 'center', padding: '40px' }}>Загрузка...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
        <h2 style={{ margin: 0 }}>💎 Инвестиционный портфель</h2>
        
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {/* Селект выбора валюты */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>
              Показать в:
            </label>
            <select
              value={targetCurrency}
              onChange={(e) => setTargetCurrency(e.target.value)}
              disabled={converting}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: converting ? 'not-allowed' : 'pointer',
                background: 'white'
              }}
            >
              <option value="RUB">₽ RUB</option>
              <option value="USD">$ USD</option>
              <option value="EUR">€ EUR</option>
            </select>
            {converting && (
              <span style={{ fontSize: '12px', color: '#6b7280' }}>⏳</span>
            )}
          </div>

          {/* Кнопка обновления всех цен */}
          <button
            onClick={handleUpdateAllPrices}
            disabled={updatingAllPrices || assets.length === 0}
            style={{
              padding: '10px 20px',
              background: updatingAllPrices ? '#9ca3af' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: updatingAllPrices || assets.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {updatingAllPrices ? '⏳ Обновление...' : '🔄 Обновить цены'}
          </button>

          <button
            onClick={() => {
              setShowAddAsset(!showAddAsset);
              // Сбрасываем состояния при закрытии
              if (showAddAsset) {
                setParseError('');
                setParseSuccess(false);
              }
            }}
            style={{
              padding: '10px 20px',
              background: '#16a34a',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            {showAddAsset ? '✕ Отмена' : '+ Добавить актив'}
          </button>
        </div>
      </div>

      {/* Результат обновления цен */}
      {updateResult && (
        <div style={{
          padding: '12px 20px',
          marginBottom: '20px',
          borderRadius: '6px',
          background: updateResult.startsWith('✅') ? '#d1fae5' : '#fee2e2',
          color: updateResult.startsWith('✅') ? '#065f46' : '#991b1b',
          border: `1px solid ${updateResult.startsWith('✅') ? '#86efac' : '#fca5a5'}`,
          fontSize: '14px',
          fontWeight: '500'
        }}>
          {updateResult}
        </div>
      )}

      {/* Общая статистика */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))',
        gap: '15px', 
        marginBottom: '20px' 
      }}>
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>Вложено</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
            {new Intl.NumberFormat('ru-RU', { style: 'currency', currency: targetCurrency }).format(totalInvested)}
          </div>
        </div>

        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>Текущая стоимость</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
            {new Intl.NumberFormat('ru-RU', { style: 'currency', currency: targetCurrency }).format(totalCurrentValue)}
          </div>
        </div>

        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>Прибыль/Убыток</div>
          <div style={{ 
            fontSize: '24px', 
            fontWeight: 'bold',
            color: totalProfit >= 0 ? '#16a34a' : '#dc2626'
          }}>
            {totalProfit >= 0 ? '+' : ''}
            {new Intl.NumberFormat('ru-RU', { style: 'currency', currency: targetCurrency }).format(totalProfit)}
          </div>
          <div style={{ fontSize: '12px', color: totalProfit >= 0 ? '#16a34a' : '#dc2626' }}>
            {profitPercent >= 0 ? '+' : ''}{profitPercent.toFixed(2)}%
          </div>
        </div>

        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>Активов</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3b82f6' }}>
            {assets.length}
          </div>
        </div>
      </div>

      {/* Форма добавления актива */}
      {showAddAsset && (
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          border: '2px solid #3b82f6',
          marginBottom: '20px'
        }}>
          <h3 style={{ marginTop: 0 }}>Добавить актив</h3>
          
          {/* Шаг 1: Парсинг ссылки */}
          <div style={{ marginBottom: '20px', padding: '15px', background: '#f0f9ff', borderRadius: '6px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              � Тикер, название или ссылка (опционально)
            </label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="text"
                placeholder="BTC, SBER, Сбербанк или https://ru.investing.com/crypto/bitcoin"
                value={assetForm.url}
                onChange={(e) => {
                  setAssetForm({ ...assetForm, url: e.target.value });
                  setParseError(''); // Очищаем ошибку при изменении
                  setParseSuccess(false); // Сбрасываем успех
                }}
                style={{ 
                  flex: 1, 
                  padding: '10px', 
                  border: parseError ? '1px solid #ef4444' : (parseSuccess ? '1px solid #10b981' : '1px solid #bfdbfe'),
                  borderRadius: '4px',
                  fontSize: '14px',
                  transition: 'border-color 0.3s ease'
                }}
              />
              <button
                type="button"
                onClick={handleParseUrl}
                disabled={parsing || !assetForm.url.trim()}
                style={{
                  padding: '10px 20px',
                  background: parseSuccess ? '#10b981' : (parsing ? '#9ca3af' : '#3b82f6'),
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: parsing ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.3s ease',
                  minWidth: '160px'
                }}
              >
                {parsing ? '⏳ Парсинг...' : (parseSuccess ? '✅ Готово!' : '🔍 Получить данные')}
              </button>
            </div>
            
            {/* Сообщение об успехе */}
            {parseSuccess && (
              <div style={{ 
                marginTop: '10px', 
                padding: '10px', 
                background: '#d1fae5', 
                border: '1px solid #10b981',
                borderRadius: '6px',
                color: '#065f46',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '18px' }}>✅</span>
                <span>Данные успешно загружены! Проверьте поля ниже.</span>
              </div>
            )}
            
            {/* Сообщение об ошибке */}
            {parseError && (
              <div style={{ 
                marginTop: '10px', 
                padding: '10px', 
                background: '#fee2e2', 
                border: '1px solid #ef4444',
                borderRadius: '6px',
                color: '#991b1b',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '18px' }}>❌</span>
                <span>{parseError}</span>
              </div>
            )}
            
            {/* Подсказка */}
            {!parseSuccess && !parseError && !parsing && (
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                💡 <strong>Быстро:</strong> Введите тикер (BTC, SBER, AAPL) — загрузка за 0.5 сек<br/>
                🔗 <strong>Или:</strong> Вставьте ссылку с investing.com для полной информации
              </div>
            )}
          </div>

          {/* Шаг 2: Форма актива */}
          <form onSubmit={handleCreateAsset}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Название *</label>
                <input
                  type="text"
                  placeholder="Bitcoin, Apple Inc., Сбербанк..."
                  value={assetForm.name}
                  onChange={(e) => setAssetForm({ ...assetForm, name: e.target.value })}
                  required
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Тикер</label>
                <input
                  type="text"
                  placeholder="BTC, AAPL, SBER..."
                  value={assetForm.ticker}
                  onChange={(e) => setAssetForm({ ...assetForm, ticker: e.target.value.toUpperCase() })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginBottom: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Тип *</label>
                <select
                  value={assetForm.type}
                  onChange={(e) => setAssetForm({ ...assetForm, type: e.target.value as any })}
                  required
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                >
                  <option value="stock">📈 Акции</option>
                  <option value="crypto">₿ Криптовалюта</option>
                  <option value="bond">📜 Облигации</option>
                  <option value="etf">📊 ETF</option>
                  <option value="currency">💱 Валюта</option>
                  <option value="commodity">🛢️ Товары</option>
                  <option value="other">📦 Другое</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Текущая цена</label>
                <input
                  type="number"
                  step="0.00000001"
                  placeholder="43250.50"
                  value={assetForm.currentPrice}
                  onChange={(e) => setAssetForm({ ...assetForm, currentPrice: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Валюта</label>
                <select
                  value={assetForm.currency}
                  onChange={(e) => handleCurrencyChange(e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                >
                  <option value="RUB">₽ RUB</option>
                  <option value="USD">$ USD</option>
                  <option value="EUR">€ EUR</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Количество</label>
                <input
                  type="number"
                  step="0.00000001"
                  placeholder="1.5"
                  value={assetForm.quantity}
                  onChange={(e) => setAssetForm({ ...assetForm, quantity: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                />
                <small style={{ color: '#6b7280', fontSize: '12px' }}>💡 Сколько единиц актива у вас есть</small>
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Заметки</label>
              <AutoTextarea
                placeholder="Дополнительная информация..."
                value={assetForm.notes}
                onChange={(e) => setAssetForm({ ...assetForm, notes: e.target.value })}
                rows={2}
                style={{ 
                  width: '100%', 
                  padding: '8px', 
                  border: '1px solid #d1d5db', 
                  borderRadius: '4px',
                  resize: 'vertical'
                }}
              />
            </div>

            <button
              type="submit"
              style={{
                width: '100%',
                padding: '12px',
                background: '#16a34a',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              ✓ Сохранить актив
            </button>
          </form>
        </div>
      )}

      {/* Список активов */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        {assets.length === 0 ? (
          <div style={{ 
            background: 'white', 
            padding: '60px', 
            borderRadius: '8px', 
            textAlign: 'center',
            border: '2px dashed #e5e7eb'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>💎</div>
            <div style={{ fontSize: '18px', color: '#1f2937', fontWeight: '500', marginBottom: '8px' }}>
              Портфель пуст
            </div>
            <div style={{ color: '#6b7280' }}>
              Добавьте первый актив, чтобы начать отслеживать инвестиции
            </div>
          </div>
        ) : (
          assets.map((asset) => (
            <div
              key={asset.id}
              style={{
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '16px'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                {/* Инфо об активе */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px' }}>
                      {asset.name}
                      {asset.ticker && (
                        <span style={{ 
                          marginLeft: '8px', 
                          fontSize: '14px', 
                          color: '#6b7280',
                          fontWeight: 'normal'
                        }}>
                          ({asset.ticker})
                        </span>
                      )}
                    </h3>
                    <span style={{
                      padding: '2px 8px',
                      background: '#f3f4f6',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: '#6b7280'
                    }}>
                      {asset.type}
                    </span>
                  </div>

                  {asset.currentPrice && (
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
                      Текущая цена: <strong>
                        {convertedPrices[asset.id] 
                          ? `${convertedPrices[asset.id].toFixed(2)} ${targetCurrency}`
                          : `${asset.currentPrice} ${asset.currency}`}
                      </strong>
                      {asset.currency !== targetCurrency && convertedPrices[asset.id] && (
                        <span style={{ marginLeft: '8px', fontSize: '12px', color: '#9ca3af' }}>
                          (исходная: {asset.currentPrice} {asset.currency})
                        </span>
                      )}
                      {asset.lastPriceUpdate && (
                        <span style={{ marginLeft: '8px', fontSize: '12px' }}>
                          (обновлено: {new Date(asset.lastPriceUpdate).toLocaleString('ru-RU')})
                        </span>
                      )}
                    </div>
                  )}

                  {/* Статистика */}
                  {asset.stats && asset.stats.totalQuantity > 0 && (
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))',
                      gap: '12px',
                      padding: '12px',
                      background: '#f9fafb',
                      borderRadius: '6px'
                    }}>
                      <div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>Количество</div>
                        <div style={{ fontWeight: '600' }}>{asset.stats.totalQuantity.toFixed(8)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>Средняя цена</div>
                        <div style={{ fontWeight: '600' }}>
                          {asset.stats.averagePrice.toFixed(2)} {asset.stats.averagePriceCurrency || asset.currency}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>Вложено</div>
                        <div style={{ fontWeight: '600' }}>
                          {new Intl.NumberFormat('ru-RU').format(asset.stats.netInvested)} {asset.stats.displayCurrency || targetCurrency}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>Текущая стоимость</div>
                        <div style={{ fontWeight: '600' }}>
                          {new Intl.NumberFormat('ru-RU').format(asset.stats.currentValue)} {asset.stats.displayCurrency || targetCurrency}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>Прибыль/Убыток</div>
                        <div style={{ 
                          fontWeight: '700',
                          color: asset.stats.profit >= 0 ? '#16a34a' : '#dc2626'
                        }}>
                          {asset.stats.profit >= 0 ? '+' : ''}
                          {new Intl.NumberFormat('ru-RU').format(asset.stats.profit)} {asset.stats.displayCurrency || targetCurrency}
                          <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                            ({asset.stats.profitPercent >= 0 ? '+' : ''}{asset.stats.profitPercent.toFixed(2)}%)
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {asset.notes && (
                    <div style={{ 
                      marginTop: '10px', 
                      fontSize: '13px', 
                      color: '#6b7280',
                      fontStyle: 'italic'
                    }}>
                      📝 {asset.notes}
                    </div>
                  )}
                </div>

                {/* Действия */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginLeft: '20px' }}>
                  <button
                    onClick={() => {
                      setSelectedAsset(asset);
                      setTransactionForm({
                        type: 'buy',
                        quantity: '',
                        price: asset.currentPrice?.toString() || '',
                        date: new Date().toISOString().split('T')[0],
                        commission: '0',
                        notes: ''
                      });
                      setShowTransactionModal(true);
                    }}
                    style={{
                      padding: '8px 16px',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    📊 Операции
                  </button>

                  <button
                    onClick={() => handleDeleteAsset(asset.id)}
                    style={{
                      padding: '8px 16px',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    🗑️ Удалить
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Модальное окно транзакции */}
      {showTransactionModal && selectedAsset && (
        <div className="mobile-dialog-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ marginTop: 0 }}>
              {transactionForm.type === 'buy' ? '📈 Купить' : '📉 Продать'} {selectedAsset.name}
            </h3>

            <form onSubmit={handleAddTransaction}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Тип операции
                </label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={() => setTransactionForm({ ...transactionForm, type: 'buy' })}
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: transactionForm.type === 'buy' ? '#16a34a' : '#f3f4f6',
                      color: transactionForm.type === 'buy' ? 'white' : '#1f2937',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Купить
                  </button>
                  <button
                    type="button"
                    onClick={() => setTransactionForm({ ...transactionForm, type: 'sell' })}
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: transactionForm.type === 'sell' ? '#dc2626' : '#f3f4f6',
                      color: transactionForm.type === 'sell' ? 'white' : '#1f2937',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Продать
                  </button>
                </div>
              </div>

              <div className="mobile-stack-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Количество *
                  </label>
                  <input
                    type="number"
                    step="0.00000001"
                    placeholder="1.5"
                    value={transactionForm.quantity}
                    onChange={(e) => setTransactionForm({ ...transactionForm, quantity: e.target.value })}
                    required
                    max={transactionForm.type === 'sell' ? selectedAsset.stats?.totalQuantity : undefined}
                    style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  />
                  {transactionForm.type === 'sell' && selectedAsset.stats && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Доступно: {selectedAsset.stats.totalQuantity.toFixed(8)}
                    </div>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Цена за 1 *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="43250.50"
                    value={transactionForm.price}
                    onChange={(e) => setTransactionForm({ ...transactionForm, price: e.target.value })}
                    required
                    style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  />
                </div>
              </div>

              {transactionForm.quantity && transactionForm.price && (
                <div style={{ 
                  padding: '12px', 
                  background: '#f0f9ff', 
                  borderRadius: '6px', 
                  marginBottom: '15px',
                  fontSize: '14px'
                }}>
                  <strong>Сумма операции:</strong>{' '}
                  {new Intl.NumberFormat('ru-RU').format(
                    parseFloat(transactionForm.quantity) * parseFloat(transactionForm.price)
                  )} {selectedAsset.currency}
                </div>
              )}

              <div className="mobile-stack-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Дата *
                  </label>
                  <input
                    type="date"
                    value={transactionForm.date}
                    onChange={(e) => setTransactionForm({ ...transactionForm, date: e.target.value })}
                    required
                    style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Комиссия
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0"
                    value={transactionForm.commission}
                    onChange={(e) => setTransactionForm({ ...transactionForm, commission: e.target.value })}
                    style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Заметки
                </label>
                <AutoTextarea
                  placeholder="Примечания к операции..."
                  value={transactionForm.notes}
                  onChange={(e) => setTransactionForm({ ...transactionForm, notes: e.target.value })}
                  rows={2}
                  style={{ 
                    width: '100%', 
                    padding: '8px', 
                    border: '1px solid #d1d5db', 
                    borderRadius: '4px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: transactionForm.type === 'buy' ? '#16a34a' : '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  {transactionForm.type === 'buy' ? '✓ Купить' : '✓ Продать'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowTransactionModal(false);
                    setSelectedAsset(null);
                  }}
                  style={{
                    padding: '12px 24px',
                    background: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestmentsPortfolio;
